import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  useGetCollaboratorList,
  useGetColleagueList,
  useGetNextStepData,
  useGetPrevStepData,
  useSendStepData,
} from "./api";
import { useAssessmentStore } from "./store";

export const useAssessmentFlow = () => {
  const { assessmentId, setAssessmentId, setStepId, showInitialStep, setShowInitialStep, stepData, setStepData } =
    useAssessmentStore();

  const [showFinal, setShowFinal] = useState(false);
  const [nextStepId, setNextStepId] = useState<string>("");

  const { data: collaboratorList, isLoading: isLoadingCollaborators } = useGetCollaboratorList(showInitialStep);
  const { data: colleagueList, isLoading: isLoadingColleagues } = useGetColleagueList(showInitialStep);

  const {
    data: nextStepData,
    refetch: refetchNextStep,
    isFetching: isLoadingNextStep,
  } = useGetNextStepData(assessmentId, nextStepId);

  const {
    data: prevStepData,
    refetch: refetchPrevStep,
    isFetching: isLoadingPrevStep,
  } = useGetPrevStepData(assessmentId, stepData?.stepId);

  const { mutateAsync: sendStepData, isPending: isSendingStepData } = useSendStepData();

  useEffect(() => {
    if (nextStepData) {
      setStepData(nextStepData);

      if (showInitialStep) {
        setShowInitialStep(false);
      }
    }
  }, [nextStepData]);

  useEffect(() => {
    if (prevStepData) {
      setStepData(prevStepData);
    }
  }, [prevStepData]);

  const resetToInitial = () => {
    setStepData(null);
    setStepId?.(null);
    setNextStepId("");
    setShowInitialStep(true);
    setAssessmentId(null);
    setShowFinal(false);
  };

  const submitStepData = async (submittedStepId: string, data: any, previousData?: any) => {
    const requestBody = { stepId: submittedStepId, data };
    const hasChanges = JSON.stringify(data) !== JSON.stringify(previousData);

    await sendStepData(
      { data: requestBody, assessmentId },
      {
        onSuccess: (response) => {
          if (hasChanges) {
            if (response.assessmentStatus === "submitted") {
              toast.success("Your review has been saved");
            } else {
              toast.success("Answer saved");
            }
          }

          if (response.assessmentStatus === "submitted") {
            setShowFinal(true);
            return;
          }

          setStepId(submittedStepId);
          nextStepId === submittedStepId ? refetchNextStep() : setNextStepId(submittedStepId);
        },
        onError: () => {
          toast.error("Failed to save answer");
        },
      },
    );
  };

  const handleGoBack = async () => {
    stepData?.stepCurrent === 1 ? resetToInitial() : await refetchPrevStep();
  };

  return {
    submitStepData,
    onLoadPrevStep: handleGoBack,

    isLoadingNextStep,
    isLoadingPrevStep,
    isSendingStepData,

    collaboratorList,
    colleagueList,

    isLoadingInitialStepData: isLoadingCollaborators || isLoadingColleagues,
    showInitialStep,
    setShowInitialStep,

    showFinal,
    resetToInitial,
  };
};
